// Custom hook for accessing budget data - routes to API in live mode, mock data in demo mode
import { useState, useEffect } from 'react';
import type {
  ProjectBudgetData,
  BudgetSummary,
  CostBudget,
  SalesBudget,
  ResourceBudget,
  AllBudgetsView,
  BudgetAlert,
  BudgetRevision,
} from '@/types/budget.types';
import { fetchBudgetData, getCostingSyncStatus, getDataMode, getApiBaseUrl, saveSalesBudget, saveCostBudget, saveResourceBudget, upsertCostCentreBudget } from '@/lib/data-service';
import type { SaveSalesBudgetData, SaveSalesCategoryData, SaveCostBudgetData, SaveResourceBudgetData } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

// ============================================================================
// MAIN BUDGET DATA HOOK
// ============================================================================

export function useBudgetData(projectId: number) {
  const [data, setData] = useState<ProjectBudgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);

    let pollInterval: ReturnType<typeof setInterval> | undefined;
    let pollTimeout: ReturnType<typeof setTimeout> | undefined;
    let noneCount = 0;
    let polling = false;

    const stopPolling = (_reason: string) => {
      if (!polling) return;
      polling = false;
      clearInterval(pollInterval);
      clearTimeout(pollTimeout);
    };

    const startPolling = () => {
      if (getDataMode() !== 'live') return;
      polling = true;
      pollInterval = setInterval(async () => {
        if (ignore) { stopPolling('component unmounted'); return; }
        try {
          const syncStatus = await getCostingSyncStatus(projectId);
          if (syncStatus.status === 'completed') {
            stopPolling('sync completed');
            // Skip re-fetch when the sync stored nothing — the data we
            // already have from the initial load is still current.
            if (syncStatus.storedCount === 0) {
              return;
            }
            try {
              const freshData = await fetchBudgetData(projectId);
              if (!ignore && freshData) {
                setData(freshData);
              }
            } catch {
              // Transient refetch error — ignore
            }
          } else if (syncStatus.status === 'failed') {
            stopPolling('sync failed');
          } else if (syncStatus.status === 'none') {
            noneCount++;
            if (noneCount >= 5) {
              stopPolling('no sync detected after 5 polls');
            }
          } else {
            noneCount = 0;
          }
        } catch {
          // Transient error — keep polling, the safety timeout will stop us eventually
        }
      }, 3000);

      pollTimeout = setTimeout(() => {
        stopPolling('safety timeout (60s)');
      }, 60000);
    };

    fetchBudgetData(projectId)
      .then((budgetData) => {
        if (ignore) return;
        if (budgetData) {
          setData(budgetData);
        } else {
          setData(null);
        }
        setLoading(false);
        // Start polling only after the initial load so we don't re-fetch
        // data we just loaded when the sync status is already "completed".
        startPolling();
      })
      .catch((err) => {
        if (ignore) return;
        console.error('[useBudgetData] Error loading budget:', err);
        setError(err.message);
        setLoading(false);
        // Still start polling on error — a background sync may fix things
        startPolling();
      });

    return () => {
      ignore = true;
      stopPolling('cleanup');
    };
  }, [projectId, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  return { data, loading, error, refetch };
}

// ============================================================================
// SPECIFIC SECTION HOOKS
// ============================================================================

export function useBudgetSummary(projectId: number) {
  const [data, setData] = useState<BudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.summary || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useCostBudget(projectId: number) {
  const [data, setData] = useState<CostBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.costBudget || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useSalesBudget(projectId: number) {
  const [data, setData] = useState<SalesBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.salesBudget || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useResourceBudget(projectId: number) {
  const [data, setData] = useState<ResourceBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.resourceBudget || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useAllBudgetsView(projectId: number) {
  const [data, setData] = useState<AllBudgetsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.allBudgets || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

export function useBudgetAlerts(projectId: number) {
  const [data, setData] = useState<BudgetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.alerts || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  const dismissAlert = (alertId: number) => {
    setData((prevData) =>
      prevData.map((alert) =>
        alert.id === alertId ? { ...alert, dismissed: true } : alert
      )
    );
  };

  return { data, loading, error, dismissAlert };
}

export function useBudgetRevisions(projectId: number) {
  const [data, setData] = useState<BudgetRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchBudgetData(projectId)
      .then((budgetData) => {
        setData(budgetData?.revisions || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  return { data, loading, error };
}

// ============================================================================
// MUTATION HOOKS (for future editing functionality)
// ============================================================================

export function useUpdateBudget() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateBudget = async (projectId: number, budgetData: Partial<ProjectBudgetData>) => {
    setLoading(true);
    setError(null);

    try {
      // Handle sales budget update
      if (budgetData.salesBudget) {
        const salesData: SaveSalesBudgetData = {
          projectId,
          projectType: budgetData.salesBudget.projectType,
          totalBudget: budgetData.salesBudget.totals?.budget || 0,
          totalActual: budgetData.salesBudget.totals?.actual || 0,
          contractValue: budgetData.salesBudget.contractValue,
          invoiced: budgetData.salesBudget.invoiced,
          recognised: budgetData.salesBudget.recognised,
          remaining: budgetData.salesBudget.remaining,
          estimated: budgetData.salesBudget.estimated,
          actual: budgetData.salesBudget.actual,
          nteAmount: budgetData.salesBudget.nteAmount,
          unbilledTime: budgetData.salesBudget.unbilledTime,
          unbilledExpenses: budgetData.salesBudget.unbilledExpenses,
          estimatedRevenue: budgetData.salesBudget.estimatedRevenue,
          monthlyRetainer: budgetData.salesBudget.monthlyRetainer,
          includedHours: budgetData.salesBudget.includedHours,
          overageRate: budgetData.salesBudget.overageRate,
          milestones: budgetData.salesBudget.milestones?.map(m => ({
            id: m.id,
            name: m.name,
            amount: m.amount,
            dueDate: m.dueDate,
            triggerPercent: m.triggerPercent,
            status: m.status,
          })),
          categories: budgetData.salesBudget.categories?.map(cat => ({
            id: cat.id,
            name: cat.name,
            code: cat.code,
            type: cat.type,
            budget: cat.budget,
            actual: cat.actual,
            scope: cat.scope,
            globalCategoryId: cat.globalCategoryId,
            isCustom: cat.isCustom,
            monthlyBudgets: cat.monthlyBudgets,
          } as SaveSalesCategoryData)),
        };
        await saveSalesBudget(salesData);
      }

      // Handle cost budget update
      if (budgetData.costBudget) {
        const costData: SaveCostBudgetData = {
          projectId,
          totalBudget: budgetData.costBudget.totals?.budget || 0,
          totalActual: budgetData.costBudget.totals?.actual,
          totalCommitted: budgetData.costBudget.totals?.committed,
          totalAvailable: budgetData.costBudget.totals?.available,
          progressPercent: budgetData.costBudget.totals?.progressPercent,
          categories: budgetData.costBudget.categories?.map(cat => ({
            id: cat.id,
            name: cat.name,
            code: cat.code,
            type: cat.type,
            budget: cat.budget,
            actual: cat.actual,
            committed: cat.committed,
            available: cat.available,
            progressPercent: cat.progressPercent,
            scope: cat.scope,
            globalCategoryId: cat.globalCategoryId,
            isCustom: cat.isCustom,
            monthlyBudgets: cat.monthlyBudgets,
          })),
        };
        await saveCostBudget(costData);
      }

      // Handle resource budget update
      if (budgetData.resourceBudget) {
        const resourceData: SaveResourceBudgetData = {
          projectId,
          totalBudget: budgetData.resourceBudget.summary?.totalBudget,
          totalActual: budgetData.resourceBudget.summary?.totalActual,
          totalRemaining: budgetData.resourceBudget.summary?.totalRemaining,
          progressPercent: budgetData.resourceBudget.summary?.progressPercent,
          billablePercent: budgetData.resourceBudget.summary?.billablePercent,
          targetBillablePercent: budgetData.resourceBudget.summary?.targetBillablePercent,
          totalBudgetCost: budgetData.resourceBudget.summary?.totalBudgetCost,
          totalActualCost: budgetData.resourceBudget.summary?.totalActualCost,
          byRole: budgetData.resourceBudget.byRole?.map(role => ({
            id: role.id,
            roleId: role.roleId,
            role: role.role,
            budgetHours: role.budgetHours,
            actualHours: role.actualHours,
            remainingHours: role.remainingHours,
            percentUsed: role.percentUsed,
            billRate: role.billRate,
            costRate: role.costRate,
            budgetCost: role.budgetCost,
            actualCost: role.actualCost,
            monthlyHours: role.monthlyHours,
            costCentreCode: role.costCentreCode,
          })),
        };
        await saveResourceBudget(resourceData);
      }

      // Persist cost-centre-level budgets that were deferred during editing
      const allCcBudgets = [
        ...(budgetData.salesBudget?.pendingCcBudgets ?? []),
        ...(budgetData.costBudget?.pendingCcBudgets ?? []),
        ...(budgetData.resourceBudget?.pendingCcBudgets ?? []),
      ];
      for (const entry of allCcBudgets) {
        await upsertCostCentreBudget(entry.costCentreId, entry.budgetType, entry.monthlyBudgets);
      }
      
      setLoading(false);
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Update failed';
      console.error('[useUpdateBudget] Error saving budget:', errorMessage);
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  return { updateBudget, loading, error };
}

export function useCreateBudgetRevision() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createRevision = async (
    projectId: number,
    revisionData: Partial<BudgetRevision>
  ) => {
    setLoading(true);
    setError(null);

    const mode = getDataMode();

    try {
      if (mode === 'demo') {
        // Simulate API call in demo mode
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        // Live mode - call the API
        await apiFetch(`${getApiBaseUrl()}/projects/${projectId}/budget/revisions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(revisionData),
        });
      }
      
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create revision failed');
      setLoading(false);
      return { success: false };
    }
  };

  return { createRevision, loading, error };
}
